/* eslint-disable react/prop-types */
import { LICENSING_TYPE } from '@/constants/licensingType'
import useAuth from '@/hooks/useAuth'
import About from '@/views/app/About'
import BreadcrumbBatches from '@/views/app/Batches/Status/Breadcrumb'
import BreadcrumbCastrations from '@/views/app/Castrations/Status/Breadcrumb'
import BreadcrumbClinicalCares from '@/views/app/ClinicalCares/Status/Breadcrumb'
import Dashboard from '@/views/app/Dashboard'
import BreadcrumbNgos from '@/views/app/ngos/Breadcrumb'
import BreadcrumbPotectors from '@/views/app/protectors/Breadcrumb'
import React, { useMemo } from 'react'
import { Route, Switch, useRouteMatch } from 'react-router-dom'
import { NotFound } from '@/components'
import { MISTREATMENT } from '@/lib/enums'
import AdoptionFairMessages from '@/views/app/AdoptionFairs/Messages'
import CompleteAccountContainer from '../components/CompleteAccountContainer'
import AdoptionFairsRoutes from './adoptionFairs'
import AdoptionsRequestedRoutes from './adoptionsRequested'
import BatchesRoutes from './batches'
import CastrationsRoutes from './castrations'
import ClinicalCaresRoutes from './clinicalCares'
import ClinicsRoutes from './clinics'
import Sidebar from './components/Sidebar'
import ManualAdoptionsRoutes from './manualAdoptions'
import MistreatmentsRoutes from './mistreatments'
import NgosRoutes from './ngos'
import PartnerBlacklistsRoutes from './partnerBlacklists'
import PetisUsersRoutes from './petisUsers'
import PetsRoutes from './pets'
import ProtectorsRoutes from './protectors'
import ReportsRoutes from './reports'
import SettingsRoutes from './settings'
import RatingsRoutes from './ratings'
import RegionsRoutes from './regions'
import ClinicalCarePrioritiesRoutes from './clinicalCarePriorities'
import ManualCastrationsRoutes from './manualCastrations'
import MistreatmentTypesRoutes from './mistreatmentTypes'
import StallsRoutes from './stalls'

function App() {
  const { path } = useRouteMatch()
  const { licensing, user } = useAuth()

  const allRoutes = [
    {
      key: 'dashboard',
      exact: true,
      breadcrumb: 'Dashboard',
      icon: 'fas fa-tachometer-alt',
      path: '/app',
    },
    {
      key: 'castrations',
      breadcrumb: 'Castrações',
      icon: 'fas fa-cut',
      permission: LICENSING_TYPE.CASTRATION_PROVIDER,
      children: [
        {
          key: 'castrationsList',
          breadcrumb: 'Castrações',
          icon: 'fas fa-cut',
          path: '/app/castrations',
          exact: true,
          permission: LICENSING_TYPE.CASTRATION_PROVIDER,
        },
        {
          key: 'castrationsMessages',
          breadcrumb: 'Mensagens',
          icon: 'fas fa-comments',
          path: '/app/castrations/messages',
          permission: LICENSING_TYPE.CASTRATION_PROVIDER,
        },
        {
          key: 'castrationsTerms',
          breadcrumb: 'Termos',
          icon: 'fas fa-file-alt',
          path: '/app/castrations/terms',
          permission: LICENSING_TYPE.CASTRATION_PROVIDER,
        },
      ],
    },
    {
      key: 'manualCastrations',
      breadcrumb: 'Castração manual',
      icon: 'fas fa-book-medical',
      path: '/app/manual-castrations',
      permission: LICENSING_TYPE.CASTRATION_PROVIDER,
    },
    {
      key: 'manualCastrationsShow',
      breadcrumb: ({ match }) => <span>Castração #{match?.params?.id}</span>,
      hide: true,
      icon: 'fas fa-book-medical',
      path: '/app/manual-castrations/show/:id',
      permission: LICENSING_TYPE.CASTRATION_PROVIDER,
    },
    {
      key: 'manualCastrationsAdd',
      breadcrumb: 'Adicionar',
      icon: 'fas fa-book-medical',
      path: '/app/manual-castrations/add',
      permission: LICENSING_TYPE.CASTRATION_PROVIDER,
      hide: true,
    },
    {
      key: 'mistreatments',
      icon: 'fas fa-bullhorn',
      breadcrumb: 'Maus tratos',
      permission: LICENSING_TYPE.MISTREATMENT,
      children: [
        {
          key: 'mistreatmentsList',
          icon: 'fas fa-bullhorn',
          path: '/app/mistreatments',
          breadcrumb: 'Denúncias',
        },
        {
          key: 'mistreatmentsTypesList',
          icon: 'fas fa-list',
          path: '/app/mistreatment-types',
          breadcrumb: 'Tipos de denúncia',
        },
        {
          key: 'mistreatmentsTypesOsmIntegration',
          icon: 'fas fa-list',
          hide: true,
          path: '/app/mistreatment-types/:id/osm-integration',
          breadcrumb: ({ match }) => `${match?.params?.id ? `#${match.params.id} ` : ''}Integração com OS-m`,
        },
      ],
    },
    {
      key: 'mistreatmentsStatus',
      icon: 'fas fa-bullhorn',
      breadcrumb: ({ match }) => <span>{MISTREATMENT.status.t(match?.params?.status)}</span>,
      path: '/app/mistreatments/status/:status',
      hide: true,
      permission: LICENSING_TYPE.MISTREATMENT,
    },
    {
      key: 'mistreatmentsShow',
      icon: 'fas fa-bullhorn',
      breadcrumb: ({ match }) => <span>Denúncia #{match?.params?.id}</span>,
      path: '/app/mistreatments/show/:id',
      hide: true,
      permission: LICENSING_TYPE.MISTREATMENT,
    },
    {
      key: 'mistreatmentsCreate',
      icon: 'fas fa-bullhorn',
      breadcrumb: 'Nova denúncia',
      path: '/app/mistreatments/form/add',
      hide: true,
      permission: LICENSING_TYPE.MISTREATMENT,
    },
    {
      key: 'clinicalCares',
      breadcrumb: 'Atendimentos',
      icon: 'fas fa-stethoscope',
      permission: LICENSING_TYPE.CLINICAL_CARE,
      children: [
        {
          key: 'clinicalCaresList',
          breadcrumb: 'Atendimentos',
          icon: 'fas fa-stethoscope',
          path: '/app/clinical-cares',
          exact: true,
          permission: LICENSING_TYPE.CLINICAL_CARE,
        },
        {
          key: 'clinicalCaresMessages',
          breadcrumb: 'Mensagens',
          icon: 'fas fa-comments',
          path: '/app/clinical-cares/messages',
          permission: LICENSING_TYPE.CLINICAL_CARE,
        },
        {
          key: 'clinicalCaresClinics',
          breadcrumb: 'Clínicas',
          icon: 'fas fa-clinic-medical',
          path: '/app/clinical-cares/clinics',
          exact: true,
          permission: LICENSING_TYPE.CLINICAL_CARE,
        },
        {
          key: 'clinicalCaresClinicsItems',
          breadcrumb: 'Gerenciar itens da clínica',
          path: '/app/clinical-cares/clinics/:id/items',
          exact: true,
          hide: true,
          permission: LICENSING_TYPE.CLINICAL_CARE,
        },

        {
          key: 'clinicalCaresClinicalCareItems',
          breadcrumb: 'Itens clínicos',
          icon: 'fas fa-star-of-life',
          path: '/app/clinical-cares/clinical-care-items',
          permission: LICENSING_TYPE.CLINICAL_CARE,
        },
        {
          key: 'clinicalCaresClassifications',
          breadcrumb: 'Classificações',
          icon: 'fas fa-tags',
          path: '/app/clinical-cares/classifications',
          permission: LICENSING_TYPE.CLINICAL_CARE,
        },
        {
          key: 'clinicalCaresClinicalCarePriorities',
          breadcrumb: 'Prioridades',
          icon: 'fas fa-exclamation',
          path: '/app/clinical-care-priorities',
          permission: LICENSING_TYPE.CLINICAL_CARE,
        },
        {
          key: 'clinicalCaresClinicalCarePanel',
          breadcrumb: 'Painel das clínicas',
          icon: 'fas fa-stream',
          path: '/app/clinical-cares/clinical-care-panel',
          permission: LICENSING_TYPE.CLINICAL_CARE,
        },
        {
          key: 'clinicalCaresTerms',
          breadcrumb: 'Termos',
          icon: 'fas fa-file-alt',
          path: '/app/clinical-cares/terms',
          permission: LICENSING_TYPE.CLINICAL_CARE,
        },
      ],
    },
    {
      key: 'clinicalCaresCreate',
      breadcrumb: 'Novo atendimento',
      icon: 'fas fa-stethoscope',
      path: '/app/clinical-cares/form/add',
      hide: true,
      permission: LICENSING_TYPE.CLINICAL_CARE,
    },

    {
      key: 'clinicalCaresShow',
      breadcrumb: ({ match }) => <span>Atendimento #{match?.params?.id}</span>,
      path: '/app/clinical-cares/status/:status/:id',
      exact: true,
      hide: true,
      permission: LICENSING_TYPE.CLINICAL_CARE,
    },
    {
      key: 'clinicalCaresStatus',
      breadcrumb: BreadcrumbClinicalCares,
      path: '/app/clinical-cares/status/:status',
      hide: true,
      permission: LICENSING_TYPE.CLINICAL_CARE,
    },
    {
      key: 'pets',
      breadcrumb: 'Pets',
      icon: 'fas fa-paw',
      path: '/app/pets',
    },
    {
      key: 'regions',
      breadcrumb: 'Regiões',
      icon: 'fas fa-map',
      path: '/app/regions',
      permission: LICENSING_TYPE.REGION,
    },
    {
      key: 'stalls',
      breadcrumb: 'Baias',
      icon: 'fas fa-warehouse',
      path: '/app/stalls',
      permission: LICENSING_TYPE.STALL_CONTROL,
    },
    {
      key: 'stallsHistory',
      breadcrumb: 'Histórico',
      icon: 'fas fa-history',
      path: '/app/stalls/:id/history',
      hide: true,
    },
    {
      key: 'regionsAdd',
      breadcrumb: 'Nova região',
      icon: 'fas fa-plus',
      path: '/app/regions/form/add',
      hide: true,
    },
    {
      key: 'regionsPolygon',
      breadcrumb: 'Demarcar região',
      icon: 'fas fa-plus',
      path: '/app/regions/:id/polygon',
      hide: true,
    },
    {
      key: 'regionsEdit',
      breadcrumb: 'Editar região',
      icon: 'fas fa-edit',
      path: '/app/regions/:id/edit',
      hide: true,
    },
    {
      key: 'fairs',
      breadcrumb: 'Feiras de adoção',
      icon: 'fas fa-heart',
      permission: LICENSING_TYPE.FAIRS_MANAGER,
      children: [
        {
          key: 'fairsList',
          breadcrumb: 'Feiras',
          icon: 'fas fa-heart',
          path: '/app/adoption-fairs',
          exact: true,
          permission: LICENSING_TYPE.FAIRS_MANAGER,
        },
        {
          key: 'fairsMessages',
          breadcrumb: 'Mensagens',
          icon: 'fas fa-comments',
          path: '/app/adoption-fairs/messages',
          permission: LICENSING_TYPE.FAIRS_MANAGER,
        },
        {
          key: 'fair',
          breadcrumb: ({ match }) => <span>Feira #{match?.params?.id}</span>,
          icon: 'fas fa-paw',
          path: '/app/adoption-fairs/:id',
          hide: true,
          permission: LICENSING_TYPE.FAIRS_MANAGER,
        },
      ],
    },
    {
      key: 'adoptionsRequestedShow',
      breadcrumb: 'Solicitação de adoção',
      path: '/app/adoption-requested/:id',
      hide: true,
    },
    {
      key: 'manual-adoptions',
      breadcrumb: 'Adoções manuais',
      icon: 'fas fa-dog',
      path: '/app/manual-adoptions',
    },
    {
      key: 'manualAdoptionCreate',
      exact: true,
      breadcrumb: 'Nova adoção',
      path: '/app/manual-adoptions/form/create',
      hide: true,
    },
    {
      key: 'manualAdoptionShow',
      breadcrumb: ({ match }) => <span>Adoção #{match?.params?.id}</span>,
      path: '/app/manual-adoptions/:id',
      exact: true,
      hide: true,
    },
    {
      key: 'castrationsShow',
      breadcrumb: ({ match }) => <span>Castração #{match?.params?.id}</span>,
      path: '/app/castrations/status/:status/:id',
      hide: true,
      permission: LICENSING_TYPE.CASTRATION_PROVIDER,
    },
    {
      key: 'castrationsStatus',
      breadcrumb: BreadcrumbCastrations,
      path: '/app/castrations/status/:status',
      hide: true,
      permission: LICENSING_TYPE.CASTRATION_PROVIDER,
    },
    {
      key: 'clinics',
      icon: 'fas fa-clinic-medical',
      breadcrumb: 'Clínicas(castração)',
      permission: LICENSING_TYPE.CASTRATION_PROVIDER,
      children: [
        {
          key: 'clinicsList',
          breadcrumb: 'Licenciadas',
          icon: 'fas fa-check',
          path: '/app/clinics/approved',
        },
        {
          key: 'clinicsRequestList',
          breadcrumb: 'Solicitações',
          icon: 'fas fa-question',
          path: '/app/clinics/requested',
        },
        {
          key: 'clinicsShow',
          breadcrumb: ({ match }) => <span>Clínica #{match?.params?.id}</span>,
          path: '/app/clinics/:status/:id',
          hide: true,
        },
      ],
    },
    {
      key: 'ratings',
      breadcrumb: 'Avaliações',
      icon: 'fas fa-fa-fw fa-star',
      path: '/app/ratings',
    },
    // {
    //   key: 'news',
    //   breadcrumb: 'Notícias',
    //   icon: 'fas fa-fa-fw fa-newspaper',
    //   path: '/app/news',
    // },
    // {
    //   key: 'newsShow',
    //   breadcrumb: 'Artigo',
    //   icon: 'fas fa-fa-fw fa-newspaper',
    //   path: '/app/news/:id',
    //   hide: true,
    // },
    // {
    //   key: 'newsCreate',
    //   breadcrumb: 'Novo artigo',
    //   icon: 'fas fa-fa-fw fa-newspaper',
    //   path: '/app/news/form/create',
    //   hide: true,
    // },
    {
      key: 'protectors',
      icon: 'fas fa-hand-holding-heart',
      breadcrumb: 'Protetores',
      children: [
        {
          key: 'protectorsList',
          breadcrumb: 'Licenciados',
          icon: 'fas fa-check',
          path: '/app/protectors/approved',
        },
        {
          key: 'protectorsRequestList',
          breadcrumb: 'Solicitações',
          icon: 'fas fa-question',
          path: '/app/protectors/requested',
        },
        {
          key: 'protectorStatus',
          breadcrumb: BreadcrumbPotectors,
          path: '/app/protectors/:status',
          hide: true,
        },
      ],
    },
    {
      key: 'ngos',
      icon: 'fas fa-building',
      breadcrumb: 'ONGs',
      children: [
        {
          key: 'ngosList',
          breadcrumb: 'Licenciadas',
          icon: 'fas fa-check',
          path: '/app/ngos/approved',
        },
        {
          key: 'ngosRequestList',
          breadcrumb: 'Solicitações',
          icon: 'fas fa-question',
          path: '/app/ngos/requested',
        },
        {
          key: 'ngosStatus',
          breadcrumb: BreadcrumbNgos,
          path: '/app/ngos/:status',
          hide: true,
        },
      ],
    },
    {
      key: 'batches',
      breadcrumb: 'Lotes',
      icon: 'fas fa-box',
      path: '/app/batches',
      permission: LICENSING_TYPE.CASTRATION_PROVIDER,
    },
    {
      key: 'petsShow',
      breadcrumb: 'Histórico do pet',
      icon: 'fas fa-paw',
      path: '/app/pets/:id',
      hide: true,
    },
    {
      key: 'batchesStatus',
      breadcrumb: BreadcrumbBatches,
      path: '/app/batches/status/:status',
      hide: true,
    },
    {
      key: 'batchesShow',
      breadcrumb: ({ match }) => <span>Lote #{match?.params?.id}</span>,
      path: '/app/batches/status/:status/:id',
      hide: true,
    },
    {
      key: 'reports',
      breadcrumb: 'Relatórios',
      icon: 'fas fa-chart-area',
      path: '/app/reports',
    },
    {
      key: 'petisUsers',
      breadcrumb: 'Usuários do Petis',
      icon: 'fas fa-users',
      path: '/app/petis-users',
    },
    {
      key: 'partnerBlacklists',
      breadcrumb: 'Bloqueios',
      icon: 'fas fa-user-slash',
      path: '/app/blacklist',
    },
    {
      key: 'petisUsersShow',
      breadcrumb: 'Histórico do usuário',
      path: '/app/petis-users/:id',
      hide: true,
    },
    {
      key: 'reportsAdoptions',
      breadcrumb: 'Adoções',
      path: '/app/reports/adoptions',
      hide: true,
    },
    {
      key: 'reportsAnimals',
      breadcrumb: 'Pets',
      path: '/app/reports/animals',
      hide: true,
    },
    {
      key: 'reportsClinicalCares',
      breadcrumb: 'Atendimento Clínico',
      path: '/app/reports/clinical-cares',
      hide: true,
    },
    {
      key: 'reportsFairs',
      breadcrumb: 'Feiras de adoção',
      path: '/app/reports/fairs',
      hide: true,
    },
    {
      key: 'reportsCastrations',
      breadcrumb: 'Castrações',
      path: '/app/reports/castrations',
      hide: true,
    },
    {
      key: 'reportsCastrationTermal',
      breadcrumb: 'Mapa térmico de castrações',
      icon: 'fas fa-map',
      path: '/app/reports/castrations/castrations-termal-map',
      hide: true,
    },
    {
      key: 'reportsCastrationsByRegion',
      breadcrumb: 'Castrações por região',
      icon: 'fas fa-map',
      path: '/app/reports/castrations/castrations-by-region',
      hide: true,
    },
    {
      key: 'reportsCastrationsAwaitingBatch',
      breadcrumb: 'Castrações aguardando lote',
      icon: 'fas fa-clinic-medical',
      path: '/app/reports/castrations/castrations-awaiting-batch',
      hide: true,
    },
    {
      key: 'reportsCastrationsByBilling',
      breadcrumb: 'Castrações por faturamento',
      icon: 'fas fa-chart-bar',
      path: '/app/reports/castrations/castrations-by-billing',
      hide: true,
    },
    {
      key: 'reportsPointMap',
      breadcrumb: 'Castrações no mapa',
      icon: 'fas fa-fa-fw fa-map-marker-alt',
      path: '/app/reports/castrations/castrations-in-map',
      hide: true,
    },
    {
      key: 'reportsClinicTime',
      breadcrumb: 'Média de atendimento',
      icon: 'fas fa-fa-fw fa-stopwatch',
      path: '/app/reports/castrations/castrations-clinic-time',
      hide: true,
    },
    {
      key: 'reportsCastrationsTime',
      breadcrumb: 'Tempo das castrações',
      icon: 'fas fa-fa-fw fa-stopwatch',
      path: '/app/reports/castrations/castrations-time',
      hide: true,
    },
    {
      key: 'reportsClinicCastrations',
      breadcrumb: 'Castrações por clínica',
      icon: 'fas fa-fa-fw fa-clinic-medical',
      path: '/app/reports/castrations/castrations-by-clinic',
      hide: true,
    },
    {
      key: 'reportsMonthCastrations',
      breadcrumb: 'Castrações por mês',
      icon: 'fas fa-fa-fw fa-calendar',
      path: '/app/reports/castrations/castrations-by-month',
      hide: true,
    },
    {
      key: 'reportsYearCastrations',
      breadcrumb: 'Castrações por ano',
      icon: 'fas fa-fa-fw fa-hourglass',
      path: '/app/reports/castrations/castrations-by-year',
      hide: true,
    },
    {
      key: 'reportsCastrationsAnnualClinicStatus',
      breadcrumb: 'Castrações anuais por clínica e status',
      icon: 'fas fa-fa-fw fa-hospital',
      path: '/app/reports/castrations/castrations-annual-by-clinic-and-status',
      hide: true,
    },
    {
      key: 'reportsCastrationsRejectedCanceled',
      breadcrumb: 'Castrações rejeitadas e canceladas',
      icon: 'fas fa-fa-fw fa-ban',
      path: '/app/reports/castrations/castrations-rejected-and-canceled',
      hide: true,
    },
    {
      key: 'reportsBreedCastrations',
      breadcrumb: 'Castrações por raça',
      icon: 'fas fa-fa-fw fa-paw',
      path: '/app/reports/castrations/castrations-by-breed',
      hide: true,
    },
    {
      key: 'reportsProtectorCastrations',
      breadcrumb: 'Castrações por protetor(a)',
      icon: 'fas fa-fa-fw fa-hand-holding-heart',
      path: '/app/reports/castrations/castrations-by-protector',
      hide: true,
    },
    {
      key: 'commentsByClinicsInApprovedCastrations',
      breadcrumb: 'Comentários das clínicas por castração aprovada',
      icon: 'fas fa-fa-fw fa-comment',
      path: '/app/reports/castrations/comments-by-clinics-in-approved-castrations',
      hide: true,
    },
    {
      key: 'reportsNGOCastrations',
      breadcrumb: 'Castrações por ONG',
      icon: 'fas fa-fa-fw fa-building',
      path: '/app/reports/castrations/castrations-by-ngo',
      hide: true,
    },
    {
      key: 'reportsCastrationsByCancelationReason',
      breadcrumb: 'Castrações por motivo de cancelamento',
      icon: 'fas fa-fa-fw fa-times',
      path: '/app/reports/castrations/castrations-by-cancelation-reasons',
      hide: true,
    },
    {
      key: 'reportsCastrationsByRejectionReason',
      breadcrumb: 'Castrações por motivo de rejeição',
      icon: 'fas fa-fa-fw fa-ban',
      path: '/app/reports/castrations/castrations-by-rejection-reasons',
      hide: true,
    },
    {
      key: 'reportsAnimalsInFair',
      breadcrumb: 'Resumo das feiras de adoção',
      icon: 'fas fa-fa-fw fa-heart',
      path: '/app/reports/fairs/animals-in-fair',
      hide: true,
    },
    {
      key: 'reportsMonthlyAdoptionsByFair',
      breadcrumb: 'Adoções mensais por feira',
      icon: 'fas fa-fa-fw fa-heart',
      path: '/app/reports/fairs/monthly-adoptions-by-fair',
      hide: true,
    },
    {
      key: 'reportsAdoptionsInFairs',
      breadcrumb: 'Adoções realizadas nas feiras',
      icon: 'far fa-fa-fw fa-heart',
      path: '/app/reports/fairs/adoptions-in-fairs',
      hide: true,
    },
    {
      key: 'reportsMonthlyAdoptionsByFairManualAndDirect',
      breadcrumb: 'Adoções mensais por feira, manuais e diretas',
      icon: 'fas fa-fa-fw fa-heart',
      path: '/app/reports/adoptions/monthly-adoptions-by-fair-manual-and-direct',
      hide: true,
    },
    {
      key: 'reportsAdoptionsList',
      breadcrumb: 'Lista de adoções',
      icon: 'far fa-fa-fw fa-heart',
      path: '/app/reports/adoptions/adoptions-list',
      hide: true,
    },
    {
      key: 'reportsAdoptionsByAgeGroup',
      breadcrumb: 'Adoções por faixa etária',
      icon: 'fas fa-fa-fw fa-blind',
      path: '/app/reports/adoptions/adoptions-by-age-group',
      hide: true,
    },
    {
      key: 'reportsAdoptionsByFamilyIncome',
      breadcrumb: 'Adoções por renda familiar',
      icon: 'fas fa-fa-fw fa-money-bill',
      path: '/app/reports/adoptions/adoptions-by-family-income',
      hide: true,
    },
    {
      key: 'reportsAdoptionsByRegion',
      breadcrumb: 'Adoções por região',
      icon: 'fas fa-fa-fw fa-map-marker-alt',
      path: '/app/reports/adoptions/adoptions-by-region',
      hide: true,
    },
    {
      key: 'reportsAnimalsTermalMap',
      breadcrumb: 'Pets no mapa',
      icon: 'fas fa-fa-fw fa-map',
      path: '/app/reports/animals/animals-termal-map',
      hide: true,
    },
    {
      key: 'reportsAnimalsPointMap',
      breadcrumb: 'Pets no mapa',
      icon: 'fas fa-fa-fw fa-marker-alt',
      path: '/app/reports/animals/animals-in-map',
      hide: true,
    },
    {
      key: 'clinicalCareItemClinicBalances',
      breadcrumb: 'Estoque dos itens clínicos',
      icon: 'fas fa-fa-fw fa-star-of-life',
      path: '/app/reports/clinical-cares/clinical-care-item-clinic-balances',
      hide: true,
    },
    {
      key: 'reportsClinicalCaresTermalMap',
      breadcrumb: 'Mapa térmico de atendimentos clínicos',
      icon: 'fas fa-fw fa-map',
      path: '/app/reports/clinical-cares/clinical-cares-termal-map',
      hide: true,
    },
    {
      key: 'reportsClinicalCaresPointMap',
      breadcrumb: 'Atendimentos clínicos no mapa',
      icon: 'fas fa-fw fa-map-marker-alt',
      path: '/app/reports/clinical-cares/clinical-cares-in-map',
      hide: true,
    },
    {
      key: 'reportsClinicalCaresByRegion',
      breadcrumb: 'Atendimentos clínicos por região',
      icon: 'fas fa-fw fa-map',
      path: '/app/reports/clinical-cares/clinical-cares-by-region',
      hide: true,
    },
    {
      key: 'reportsClinicalCaresByClinic',
      breadcrumb: 'Atendimentos clínicos por clínica',
      icon: 'fas fa-fw fa-clinic-medical',
      path: '/app/reports/clinical-cares/clinical-cares-by-clinic',
      hide: true,
    },
    {
      key: 'reportsClinicalCaresByMonth',
      breadcrumb: 'Atendimentos clínicos por mês',
      icon: 'fas fa-fw fa-calendar',
      path: '/app/reports/clinical-cares/clinical-cares-by-month',
      hide: true,
    },
    {
      key: 'reportsClinicalCaresByYear',
      breadcrumb: 'Atendimentos clínicos por ano',
      icon: 'fas fa-fw fa-hourglass-half',
      path: '/app/reports/clinical-cares/clinical-cares-by-year',
      hide: true,
    },
    {
      key: 'reportsClinicalCaresByBreed',
      breadcrumb: 'Atendimentos clínicos por raça e espécie',
      icon: 'fas fa-fw fa-paw',
      path: '/app/reports/clinical-cares/clinical-cares-by-breed',
      hide: true,
    },
    {
      key: 'reportsClinicalCaresByClassification',
      breadcrumb: 'Atendimentos clínicos por classificação',
      icon: 'fas fa-fw fa-tags',
      path: '/app/reports/clinical-cares/clinical-cares-by-classification',
      hide: true,
    },
    {
      key: 'reportsClinicalCaresByPriority',
      breadcrumb: 'Atendimentos clínicos por prioridade',
      icon: 'fas fa-fw fa-exclamation-triangle',
      path: '/app/reports/clinical-cares/clinical-cares-by-priority',
      hide: true,
    },
    {
      key: 'reportsClinicalCaresByUser',
      breadcrumb: 'Atendimentos clínicos por usuário',
      icon: 'fas fa-fw fa-user',
      path: '/app/reports/clinical-cares/clinical-cares-by-user',
      hide: true,
    },
    {
      key: 'castrationsByUser',
      breadcrumb: 'Castrações por usuário',
      icon: 'fas fa-fa-fw fa-cut',
      path: '/app/reports/castrations/castrations-by-user',
      hide: true,
    },
    {
      key: 'reportsCastrationsByCras',
      breadcrumb: 'Castrações solicitadas por usuários com CRAS',
      icon: 'fas fa-fa-fw fa-user-circle',
      path: '/app/reports/castrations/castrations-by-cras',
      hide: true,
    },
    {
      key: 'castrationsRegistry',
      breadcrumb: 'Registro de castrações',
      icon: 'fas fa-fa-fw fa-cut',
      path: '/app/reports/castrations/castrations-registry',
      hide: true,
    },
    {
      key: 'castrationsOperationalSummary',
      breadcrumb: 'Resumo operacional de castrações',
      icon: 'fas fa-fa-fw fa-list-ol',
      path: '/app/reports/castrations/castrations-operational-summary',
      hide: true,
    },
    {
      key: 'settings',
      icon: 'fas fa-cog',
      breadcrumb: 'Configurações',
      hide: licensing?.licensedId !== user?.id,
      children: [
        {
          key: 'settingsInfo',
          breadcrumb: 'Institucional',
          icon: 'fas fa-info',
          path: '/app/settings/info',
        },
        {
          key: 'settingsUsers',
          breadcrumb: 'Meus usuários',
          icon: 'fas fa-users',
          path: '/app/settings/users',
        },
        {
          key: 'settingsUsersCreate',
          breadcrumb: 'Novo usuário',
          hide: true,
          path: '/app/settings/users/create',
        },
        {
          key: 'settingsParams',
          breadcrumb: 'Parâmetros',
          icon: 'fas fa-cogs',
          path: '/app/settings/params',
        },
        {
          key: 'settingsNgos',
          breadcrumb: "ONG's",
          icon: 'fas fa-building',
          path: '/app/settings/ngos',
        },
        {
          key: 'settingsProtectors',
          breadcrumb: 'Protetores',
          icon: 'fas fa-hand-holding-heart',
          path: '/app/settings/protectors',
        },
        {
          key: 'settingsProfile',
          breadcrumb: 'Meus dados',
          icon: 'fas fa-user-edit',
          path: '/app/settings/profile',
        },
        {
          key: 'settingsAccount',
          breadcrumb: 'Minha conta',
          icon: 'fas fa-user-cog',
          path: '/app/settings/account',
        },
        {
          key: 'settingsAccountResetPassword',
          breadcrumb: 'Redefinir senha',
          hide: true,
          path: '/app/settings/account/reset-password',
        },
        {
          key: 'settingsAccountChangeEmail',
          breadcrumb: 'Alterar e-mail',
          hide: true,
          path: '/app/settings/account/change-email',
        },
        {
          key: 'clinicsShow',
          breadcrumb: ({ match }) => <span>Clínica #{match?.params?.id}</span>,
          path: '/app/clinics/:status/:id',
          hide: true,
        },
      ],
    },
    {
      key: 'about',
      breadcrumb: 'Sobre',
      icon: 'fas fa-info-circle',
      path: '/app/about',
    },
  ]

  const permissions = (licensing?.licenseTypes || []).filter(
    (p) => (licensing?.myPermissions || []).includes(p),
  )

  const routes = useMemo(() => {
    if (!licensing) return []
    return allRoutes.filter((route) => {
      if (!route.permission) return true
      return (permissions || []).includes(route.permission)
    }).map((r) => {
      if (!r.children) return r

      return {
        ...r,
        children: r.children.filter((c) => {
          if (!c.permission) return true
          return (permissions || []).includes(c.permission)
        }),
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licensing])

  return (
    <Sidebar routes={routes}>
      <CompleteAccountContainer>
        <Switch>
          <Route exact path={path}>
            <Dashboard title="Petis - Parceiros / Dashboard" />
          </Route>
          <Route path={`${path}/castrations`}>
            <CastrationsRoutes />
          </Route>
          <Route path={`${path}/clinics`}>
            <ClinicsRoutes />
          </Route>
          <Route path={`${path}/ratings`}>
            <RatingsRoutes />
          </Route>
          <Route path={`${path}/mistreatments`}>
            <MistreatmentsRoutes />
          </Route>
          <Route path={`${path}/mistreatment-types`}>
            <MistreatmentTypesRoutes />
          </Route>
          <Route path={`${path}/regions`}>
            <RegionsRoutes />
          </Route>
          <Route path={`${path}/stalls`}>
            <StallsRoutes />
          </Route>
          <Route exact path={[`${path}/adoption-fairs/messages`, `${path}/adoption-fairs/messages/:conversationId`]}>
            <AdoptionFairMessages
              title="Petis - Parceiros / Feiras de adoção / Mensagens"
            />
          </Route>
          <Route path={`${path}/adoption-fairs`}>
            <AdoptionFairsRoutes />
          </Route>
          <Route path={`${path}/adoption-requested`}>
            <AdoptionsRequestedRoutes />
          </Route>
          <Route path={`${path}/manual-adoptions`}>
            <ManualAdoptionsRoutes />
          </Route>
          <Route path={`${path}/manual-castrations`}>
            <ManualCastrationsRoutes />
          </Route>
          <Route path={`${path}/blacklist`}>
            <PartnerBlacklistsRoutes />
          </Route>
          <Route path={`${path}/protectors`}>
            <ProtectorsRoutes />
          </Route>
          <Route path={`${path}/clinical-cares`}>
            <ClinicalCaresRoutes />
          </Route>
          <Route path={`${path}/clinical-care-priorities`}>
            <ClinicalCarePrioritiesRoutes />
          </Route>
          <Route path={`${path}/pets`}>
            <PetsRoutes />
          </Route>
          <Route path={`${path}/ngos`}>
            <NgosRoutes />
          </Route>
          <Route path={`${path}/batches`}>
            <BatchesRoutes />
          </Route>
          <Route path={`${path}/reports`}>
            <ReportsRoutes />
          </Route>
          <Route path={`${path}/petis-users`}>
            <PetisUsersRoutes />
          </Route>
          <Route path={`${path}/settings`}>
            <SettingsRoutes />
          </Route>
          <Route path={`${path}/about`}>
            <About />
          </Route>
          <Route>
            <NotFound />
          </Route>
        </Switch>
      </CompleteAccountContainer>
    </Sidebar>
  )
}

export default App
